import Dexie, { type Table } from 'dexie';
import type { PlatformScope } from '@examflow/core';
import { buildExamSessionScopeFields } from './examSessionScope';

export interface StoredUserAnswer {
  questionId: string;
  answer: string;
  isCorrect: boolean;
  timeSpent: number;
  sectionId?: string;
}

export interface ActiveSession {
  id: string;
  examType: string;
  languageId?: string | null;
  locale?: string | null;
  ttsLocale?: string | null;
  platformScope?: PlatformScope | null;
  mode: 'exam' | 'practice';
  questionIds: string[];
  userAnswers: StoredUserAnswer[];
  currentIndex: number;
  remainingTime: number;
  startedAt: number;
  updatedAt: number;
  bankIds?: string[];
  sectionId?: string;
  difficultyFilter?: string;
  /** Practice launch fingerprint — writing task filter. */
  taskNumber?: number | null;
  /** Practice launch fingerprint — content topic filter. */
  topicId?: string | null;
  /** Practice launch fingerprint — training intensity. */
  intensity?: string | null;
  /** Practice launch fingerprint — pack size target (`all` or number). */
  questionCountTarget?: number | 'all' | null;
  sectionBoundaries?: {
    sectionId: string;
    sectionName: string;
    startIndex: number;
    endIndex: number;
  }[];
}

export interface ExamRecord {
  id: string;
  examType: string;
  languageId?: string | null;
  locale?: string | null;
  ttsLocale?: string | null;
  platformScope?: PlatformScope | null;
  bankIds: string[];
  totalQuestions: number;
  correctCount: number;
  score: number;
  totalScore: number;
  passed: boolean;
  duration: number;
  completedAt: number;
  questionIds?: string[];
  userAnswers?: StoredUserAnswer[];
}

export type ActiveSessionLookup = {
  examType: string;
  languageId?: string | null;
  mode?: 'exam' | 'practice';
  sectionId?: string | null;
};

class ExamSessionDatabase extends Dexie {
  activeSessions!: Table<ActiveSession>;
  examRecords!: Table<ExamRecord>;

  constructor() {
    super('ExamFlowSessionDB');
    this.version(1).stores({
      activeSessions: 'id, examType, updatedAt',
      examRecords: 'id, examType, completedAt',
    });
    // PlatformScope isolation: compound index so FR TCF and EN IELTS never share a slot.
    this.version(2).stores({
      activeSessions: 'id, examType, languageId, [languageId+examType], updatedAt',
      examRecords: 'id, examType, languageId, [languageId+examType], completedAt',
    });
  }
}

const db = new ExamSessionDatabase();

function matchesLanguage(
  session: { languageId?: string | null; platformScope?: PlatformScope | null },
  languageId: string | null | undefined
): boolean {
  if (!languageId) return true;
  if (!session.languageId) {
    // Legacy rows: accept only when exam's default language matches via platformScope if present.
    return session.platformScope?.languageId === languageId;
  }
  return session.languageId === languageId;
}

export const examSessionRepository = {
  async getSessionById(sessionId: string): Promise<ActiveSession | null> {
    try {
      const session = await db.activeSessions.get(sessionId);
      return session || null;
    } catch (error) {
      console.error('[examSessionRepository] 获取会话失败:', error);
      return null;
    }
  },

  /**
   * Lookup in-progress quiz session (Mock or Training).
   * Prefer `(languageId, examType)` PlatformScope key; fall back to examType-only for legacy rows.
   * When `mode` / `sectionId` are set, filter candidates — practice must not steal mock slots.
   */
  async getActiveSession(
    examTypeOrLookup?: string | ActiveSessionLookup
  ): Promise<ActiveSession | null> {
    try {
      const lookup: ActiveSessionLookup | null =
        typeof examTypeOrLookup === 'string'
          ? { examType: examTypeOrLookup }
          : examTypeOrLookup ?? null;

      const matchesFilters = (session: ActiveSession): boolean => {
        if (lookup?.mode && session.mode !== lookup.mode) return false;
        if (lookup?.sectionId !== undefined) {
          const want = lookup.sectionId?.trim() || null;
          const got = session.sectionId?.trim() || null;
          if (want !== got) return false;
        }
        return true;
      };

      if (!lookup?.examType) {
        const sessions = await db.activeSessions.orderBy('updatedAt').reverse().toArray();
        return sessions.find(matchesFilters) || null;
      }

      const languageId = lookup.languageId?.trim() || null;
      if (languageId) {
        const scopedList = await db.activeSessions
          .where('[languageId+examType]')
          .equals([languageId, lookup.examType])
          .toArray();
        const scoped = scopedList
          .filter(matchesFilters)
          .sort((a, b) => b.updatedAt - a.updatedAt)[0];
        if (scoped) return scoped;
      }

      const candidates = await db.activeSessions.where('examType').equals(lookup.examType).toArray();
      const matched = candidates
        .filter((session) => matchesLanguage(session, languageId) && matchesFilters(session))
        .sort((a, b) => b.updatedAt - a.updatedAt)[0];
      return matched || null;
    } catch (error) {
      console.error('[examSessionRepository] 获取进行中会话失败:', error);
      return null;
    }
  },

  async saveActiveSession(session: ActiveSession): Promise<void> {
    try {
      const scopeFields = buildExamSessionScopeFields(session.examType, session);
      await db.activeSessions.put({
        ...session,
        ...scopeFields,
        languageId: session.languageId ?? scopeFields.languageId,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('[examSessionRepository] 保存会话失败:', error);
      throw error;
    }
  },

  async updateActiveSession(
    sessionId: string,
    updates: Partial<Pick<ActiveSession, 'userAnswers' | 'currentIndex' | 'remainingTime'>>
  ): Promise<void> {
    try {
      const session = await db.activeSessions.get(sessionId);
      if (!session) return;
      await db.activeSessions.put({
        ...session,
        ...updates,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('[examSessionRepository] 更新会话失败:', error);
      throw error;
    }
  },

  async deleteActiveSession(sessionId: string): Promise<void> {
    try {
      await db.activeSessions.delete(sessionId);
    } catch (error) {
      console.error('[examSessionRepository] 删除会话失败:', error);
      throw error;
    }
  },

  async saveExamRecord(record: ExamRecord): Promise<void> {
    try {
      await db.examRecords.add({
        ...record,
        ...buildExamSessionScopeFields(record.examType, record),
      });
    } catch (error) {
      console.error('[examSessionRepository] 保存考试记录失败:', error);
      throw error;
    }
  },

  async getExamRecordById(recordId: string): Promise<ExamRecord | null> {
    try {
      const record = await db.examRecords.get(recordId);
      return record || null;
    } catch (error) {
      console.error('[examSessionRepository] 获取考试记录失败:', error);
      return null;
    }
  },

  async getExamRecords(limit: number = 10): Promise<ExamRecord[]> {
    try {
      return db.examRecords.orderBy('completedAt').reverse().limit(limit).toArray();
    } catch (error) {
      console.error('[examSessionRepository] 获取考试记录失败:', error);
      return [];
    }
  },

  generateId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  },

  async getLatestExamState(
    examType: string,
    languageId?: string | null
  ): Promise<{
    status: 'in_progress' | 'completed' | 'none';
    session?: ActiveSession;
    record?: ExamRecord;
  }> {
    try {
      const session = await this.getActiveSession({ examType, languageId, mode: 'exam' });
      if (session && session.mode === 'exam') {
        return { status: 'in_progress', session };
      }
      let records = await db.examRecords.where('examType').equals(examType).sortBy('completedAt');
      if (languageId) {
        records = records.filter((record) => matchesLanguage(record, languageId));
      }
      const latestRecord = records.length > 0 ? records[records.length - 1] : null;
      if (latestRecord) {
        return { status: 'completed', record: latestRecord };
      }
      return { status: 'none' };
    } catch (error) {
      console.error('[examSessionRepository] 获取最新状态失败:', error);
      return { status: 'none' };
    }
  },

  async getAllExamRecords(
    examType?: string,
    languageId?: string | null
  ): Promise<
    Array<
      | { type: 'session'; id: string; session: ActiveSession }
      | { type: 'record'; id: string; record: ExamRecord }
    >
  > {
    try {
      const result: Array<
        | { type: 'session'; id: string; session: ActiveSession }
        | { type: 'record'; id: string; record: ExamRecord }
      > = [];

      const session = examType
        ? await this.getActiveSession({ examType, languageId })
        : await this.getActiveSession();
      if (session && session.mode === 'exam') {
        result.push({ type: 'session', id: session.id, session });
      }

      let records: ExamRecord[];
      if (examType) {
        records = await db.examRecords.where('examType').equals(examType).toArray();
        records.sort((a, b) => b.completedAt - a.completedAt);
      } else {
        records = await db.examRecords.orderBy('completedAt').reverse().toArray();
      }
      if (languageId) {
        records = records.filter((record) => matchesLanguage(record, languageId));
      }
      for (const record of records) {
        result.push({ type: 'record', id: record.id, record });
      }

      return result;
    } catch (error) {
      console.error('[examSessionRepository] 获取考试记录列表失败:', error);
      return [];
    }
  },
};
